<template>
    <div v-if="!canRead" class="admin-role-editor">
        <div class="no-permission-message">
            Sie haben keine Berechtigung, Rollen zu öffnen.
        </div>
    </div>

    <div v-else class="admin-role-editor">
        <div class="editor-toolbar">
            <DxButton icon="add" text="Neu" @click="createNew" :disabled="!canWrite" />
            <DxButton icon="save"
                      text="Speichern"
                      type="default"
                      @click="save"
                      :disabled="!canWrite || !hasChanges || role?.isSystemRole || role?.name == ''" />
            <DxButton icon="trash"
                      text="Löschen"
                      :disabled="!canDelete || !role?._id || role?.isSystemRole"
                      @click="remove" />
        </div>

        <div class="admin-role-content" v-if="role">
            <div class="admin-role-form-panel mobile-scroll">
                <DxForm :form-data="role"
                        label-mode="outside"
                        label-location="top"
                        :col-count="2"
                        class="admin-role-form">
                    <DxSimpleItem data-field="name"
                                  editor-type="dxTextBox"
                                  col-span="2"
                                  :label="{text:'Name'}"
                                  :is-required="true"
                                  :editor-options="{ readOnly: !canWrite, disabled: !canWrite }" />

                    <DxSimpleItem data-field="description"
                                  editor-type="dxTextArea"
                                  :col-span="2"
                                  :label="{text:'Beschreibung'}"
                                  :editor-options="{
                                      readOnly: !canWrite,
                                      disabled: !canWrite,
                                      minHeight: 120,
                                      autoResizeEnabled: true
                                  }" />

                    <DxSimpleItem data-field="isSuperAdmin"
                                  :label="{text:'Super-Admin'}"
                                  editor-type="dxCheckBox"
                                  :editor-options="{ readOnly: !canWrite, disabled: !canWrite }" />
                </DxForm>
            </div>

            <div class="admin-role-permissions-panel mobile-scroll">
                <div class="permissions-header">
                    <div class="permissions-title">Berechtigungen</div>
                    <DxButton text="Alle setzen"
                              icon="check"
                              @click="selectAllPermissions"
                              :disabled="!canWrite || role.isSuperAdmin" />
                    <DxButton text="Alle entfernen"
                              icon="clear"
                              @click="clearAllPermissions"
                              :disabled="!canWrite || role.isSuperAdmin" />
                </div>

                <div v-if="role.isSuperAdmin" class="super-admin-hint">
                    Diese Rolle ist als Super-Admin markiert und besitzt automatisch alle Berechtigungen.
                </div>

                <div v-for="areaGroup in groupedPermissions"
                     :key="areaGroup.area"
                     class="permission-area">
                    <div class="permission-area-title">{{ areaGroup.area }}</div>

                    <div v-for="groupGroup in areaGroup.groups"
                         :key="groupGroup.group"
                         class="permission-group">
                        <div class="permission-group-title">{{ groupGroup.group }}</div>

                        <div class="permission-items">
                            <DxCheckBox v-for="permission in groupGroup.items"
                                        :key="permission._id || permission.key"
                                        :text="permission.label || permission.key"
                                        :value="hasPermission(permission.key)"
                                        :disabled="!canWrite || role.isSuperAdmin"
                                        @value-changed="setPermission(permission.key, $event.value)" />
                        </div>
                    </div>
                </div>

                <div v-if="groupedPermissions.length === 0" class="no-data-message">
                    Keine Berechtigungsdefinitionen vorhanden.
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="js">
    import { defineComponent } from 'vue';
    import DxButton from 'devextreme-vue/button';
    import DxCheckBox from 'devextreme-vue/check-box';
    import { DxForm, DxSimpleItem } from 'devextreme-vue/form';
    import roleApi from '../services/RoleApi';
    import permissionDefinitionApi from '../services/PermissionApi';
    import meApi from '../services/MeApi';
    import AppPermissions from '../constants/AppPermissions';
    import { alert } from 'devextreme/ui/dialog'  

    export default defineComponent({
        components: {
            DxButton,
            DxCheckBox,
            DxForm,
            DxSimpleItem
        },
        emits: ['saved'],
        data() {
            return {
                role: null,
                roleOrig: null,
                permissionDefinitions: []
            };
        },
        computed: {
            canRead() {
                return meApi.hasPermissionSync(AppPermissions.AdminRoles.Read);
            },
            canWrite() {
                return meApi.hasPermissionSync(AppPermissions.AdminRoles.Write);
            },
            canDelete() {
                return meApi.hasPermissionSync(AppPermissions.AdminRoles.Delete);
            },
            hasChanges() {
                if (!this.role || this.roleOrig == null)
                    return false;
                return JSON.stringify(this.role) !== this.roleOrig;
            },
            groupedPermissions() {
                const groupsByArea = {};

                for (const permission of this.permissionDefinitions) {
                    const area = permission.area || 'Sonstiges';
                    const group = permission.group || 'Allgemein';

                    if (!groupsByArea[area]) {
                        groupsByArea[area] = {};
                    }

                    if (!groupsByArea[area][group]) {
                        groupsByArea[area][group] = [];
                    }

                    groupsByArea[area][group].push(permission);
                }

                return Object.keys(groupsByArea)
                    .sort((a, b) => a.localeCompare(b))
                    .map(area => ({
                        area,
                        groups: Object.keys(groupsByArea[area])
                            .sort((a, b) => a.localeCompare(b))
                            .map(group => ({
                                group,
                                items: groupsByArea[area][group].sort((a, b) =>
                                    ((a.sortOrder || 0) - (b.sortOrder || 0)) ||
                                    (a.label || a.key || '').localeCompare(b.label || b.key || '')
                                )
                            }))
                    }));
            }
        },
        watch: {
            '$route.params.id': {
                immediate: true,
                async handler() {
                    if (!this.canRead)
                        return;
                    await this.loadData();
                }
            }
        },
        async created() {
            if (!this.canRead)
                return;

            this.permissionDefinitions = await permissionDefinitionApi.getAll();
        },
        methods: {
            async loadData() {
                const id = this.$route.params.id;

                if (!id || id === 'new') {
                    this.role = await roleApi.createNew({
                        name: '',
                        description: '',
                        isSuperAdmin: false,
                        isSystemRole: false,
                        permissionKeys: []
                    });
                    if (!Array.isArray(this.role.permissionKeys)) {
                        this.role.permissionKeys = [];
                    }
                    this.roleOrig = JSON.stringify(this.role);
                    return;
                }

                this.role = await roleApi.get(id);

                if (!Array.isArray(this.role.permissionKeys)) {
                    this.role.permissionKeys = [];
                }

                this.roleOrig = JSON.stringify(this.role);
            },

            hasPermission(permissionKey) {
                return Array.isArray(this.role?.permissionKeys) &&
                    this.role.permissionKeys.includes(permissionKey);
            },

            setPermission(permissionKey, enabled) {
                if (!this.canWrite)
                    return;

                if (!Array.isArray(this.role.permissionKeys)) {
                    this.role.permissionKeys = [];
                }

                const index = this.role.permissionKeys.indexOf(permissionKey);

                if (enabled) {
                    if (index < 0) {
                        this.role.permissionKeys.push(permissionKey);
                    }
                } else {
                    if (index >= 0) {
                        this.role.permissionKeys.splice(index, 1);
                    }
                }

                this.role.permissionKeys = [...this.role.permissionKeys].sort((a, b) => a.localeCompare(b));
            },

            selectAllPermissions() {
                if (!this.canWrite)
                    return;

                this.role.permissionKeys = this.permissionDefinitions
                    .map(p => p.key)
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
            },

            clearAllPermissions() {
                if (!this.canWrite)
                    return;

                this.role.permissionKeys = [];
            },

            async createNew() {
                if (!this.canWrite)
                    return;

                this.$router.push('/Admin/Roles/new');
            },

            async save() {
                if (!this.canWrite || !this.hasChanges)
                    return;

                const saved = await roleApi.save(this.role);
                this.role = saved;

                if (!Array.isArray(this.role.permissionKeys)) {
                    this.role.permissionKeys = [];
                }

                this.roleOrig = JSON.stringify(this.role);

                this.$emit('saved');

                if (saved._id && this.$route.params.id !== saved._id) {
                    this.$router.replace('/Admin/Roles/' + saved._id);
                }
            },

            async remove() {
                if (!this.canDelete)
                    return;

                if (!this.role?._id)
                    return;

                try {
                    await roleApi.delete(this.role._id);
                    this.$emit('saved');
                    this.$router.replace('/Admin/Roles/new');
                }
                catch (err) {
                    let message =
                        err?.response?.data?.error ||
                        err?.response?.data?.message ||
                        err?.message ||
                        "Unbekannter Fehler";

                    alert(message);
                }
                
            }
        }
    });
</script>

<style scoped>
    .admin-role-editor {
        display: grid;
        grid-template-columns: minmax(0,1fr);
        grid-template-rows: max-content minmax(0,1fr);
        gap: 10px;
        padding: 10px;
        background-color: #2a2a2a;
        height: 100%;
        box-sizing: border-box;
    }

    .editor-toolbar {
        display: flex;
        gap: 10px;
    }

    .admin-role-content {
        display: grid;
        grid-template-columns: minmax(280px, 420px) minmax(0,1fr);
        gap: 20px;
        min-height: 0;
    }

    .admin-role-form-panel,
    .admin-role-permissions-panel {
        min-height: 0;
    }

    .permissions-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 15px;
        flex-wrap: wrap;
    }

    .permissions-title {
        font-size: 18px;
        font-weight: 600;
        margin-right: auto;
    }

    .super-admin-hint {
        margin-bottom: 15px;
        opacity: 0.8;
    }

    .permission-area {
        margin-bottom: 20px;
    }

    .permission-area-title {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 8px;
        padding-bottom: 4px;
        border-bottom: 1px solid rgba(255,255,255,0.12);
    }

    .permission-group {
        margin-bottom: 14px;
    }

    .permission-group-title {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
        opacity: 0.9;
    }

    .permission-items {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 8px 16px;
    }

    .no-permission-message {
        padding: 20px;
    }
</style>